import { c as _c } from "react/compiler-runtime";
import { useHasValidFeatureProjectLevel } from "@hooks/feature-control/use-has-user-valid-feature-project-level";
import { useHasUserValidPermissionProjectLevel } from "@hooks/permission-control/use-has-user-valid-permission-project-level";
import { useProjectArchiveDialog } from "@hooks/projects/use-project-archive-dialog";
import { appStateSelector } from "@store/app/app-selector";
import { isProjectActiveSelector, isProjectArchivingAllowedSelector, isProjectUnarchivingAllowedSelector } from "@store/projects/projects-selector";
import { useAppSelector } from "@store/store-helper";
import { useCompanyId } from "@context-providers/company-id-provider";
import { isProjectDownloaded, isProjectReadyForVaultDownload, openInSphereVault as openProjectInSphereVault } from "@utils/project-utils";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useMemo } from "react";
/**
 * Hook that provides reusable project actions like share, archive, etc.
 * Can be used in both buttons and menu items
 */
export function useProjectActions(project) {
  const $ = _c(32);
  const {
    trackEvent
  } = useTrackEvent();
  let t0;
  if ($[0] !== project) {
    t0 = {
      project
    };
    $[0] = project;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const archiveOrUnarchiveProject = useProjectArchiveDialog(t0);
  let t1;
  if ($[2] !== project.id) {
    t1 = isProjectActiveSelector(project.id);
    $[2] = project.id;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const isProjectActive = useAppSelector(t1);
  let t2;
  if ($[4] !== project) {
    t2 = isProjectArchivingAllowedSelector(project);
    $[4] = project;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const isArchivingAllowed = useAppSelector(t2);
  let t3;
  if ($[6] !== project) {
    t3 = isProjectUnarchivingAllowedSelector(project);
    $[6] = project;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const isUnarchivingAllowed = useAppSelector(t3);
  let t4;
  if ($[8] !== project) {
    t4 = {
      project
    };
    $[8] = project;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const {
    canDeleteProject
  } = useHasUserValidPermissionProjectLevel(t4);
  const {
    canArchiveProject,
    canUnarchiveProject
  } = useHasValidFeatureProjectLevel();
  const {
    isBetaTestingEnabled
  } = useAppSelector(appStateSelector);
  const companyId = useCompanyId();
  let t5;
  if ($[10] !== canArchiveProject || $[11] !== canUnarchiveProject || $[12] !== isProjectActive || $[13] !== project) {
    t5 = isProjectActive ? canArchiveProject : canUnarchiveProject && !isProjectDownloaded(project);
    $[10] = canArchiveProject;
    $[11] = canUnarchiveProject;
    $[12] = isProjectActive;
    $[13] = project;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const isAllowedToArchiveOrUnarchive = t5;
  const isArchiveActionDisabled = isProjectActive ? !isArchivingAllowed : !isUnarchivingAllowed;
  const archiveActionLabel = isProjectActive ? "Archive" : "Unarchive";
  let t6;
  if ($[15] !== isBetaTestingEnabled || $[16] !== project.archivingState) {
    t6 = isBetaTestingEnabled && isProjectReadyForVaultDownload(project.archivingState);
    $[15] = isBetaTestingEnabled;
    $[16] = project.archivingState;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const canDownloadFromVault = t6;
  let t7;
  if ($[18] !== companyId || $[19] !== project.id || $[20] !== project.name || $[21] !== trackEvent) {
    t7 = function openInSphereVault() {
      openProjectInSphereVault({
        projectId: project.id,
        projectName: project.name,
        companyId,
        trackEvent
      });
    };
    $[18] = companyId;
    $[19] = project.id;
    $[20] = project.name;
    $[21] = trackEvent;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  const openInSphereVault = t7;
  let t8;
  if ($[23] !== archiveActionLabel || $[24] !== archiveOrUnarchiveProject || $[25] !== canDeleteProject || $[26] !== canDownloadFromVault || $[27] !== isAllowedToArchiveOrUnarchive || $[28] !== isArchiveActionDisabled || $[29] !== isProjectActive || $[30] !== openInSphereVault) {
    t8 = {
      archiveOrUnarchiveProject,
      isProjectActive,
      isAllowedToArchiveOrUnarchive,
      isArchiveActionDisabled,
      canDeleteProject,
      archiveActionLabel,
      canDownloadFromVault,
      openInSphereVault
    };
    $[23] = archiveActionLabel;
    $[24] = archiveOrUnarchiveProject;
    $[25] = canDeleteProject;
    $[26] = canDownloadFromVault;
    $[27] = isAllowedToArchiveOrUnarchive;
    $[28] = isArchiveActionDisabled;
    $[29] = isProjectActive;
    $[30] = openInSphereVault;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  return t8;
}